/**
 * ACP-2092 (Section 508): the word a screen reader has to hear for a required field.
 *
 * `aria-required` is the correct way to express the state, and Chrome does expose it —
 * but JAWS 2026 does not speak it. Measured on the real product markup by tabbing four
 * variants of the same field and reading JAWS's own Braille and Text Viewer: our
 * combobox, the same plus `aria-controls`, and even a native `<select required>` were
 * all announced without the word. Only the variant carrying "required" in its
 * accessible name was spoken as "... receive? required - Select an option -".
 *
 * So the state has to be part of the name. PR #492 removed it — correctly killing the
 * `"...?requiredInformation - Select an option -"` noise, where the asterisk and the
 * tooltip icon both leaked in — and leaned on `aria-required` alone. This puts the word
 * back, on its own, without the tooltip noise coming with it.
 *
 * `aria-required` stays on every control: it is what the spec asks for and what other
 * assistive tech reads.
 */
export const REQUIRED_STATE_TEXT = 'required';

export interface RequiredStateTextProps {
  /** Referenced from the control's `aria-labelledby`, where the name is composed there. */
  id?: string;
}

/**
 * Screen-reader-only "required", to sit inside a field's label.
 *
 * Deliberately not `aria-hidden`, unlike the visual asterisk beside it: the asterisk is
 * decorative and this carries the meaning.
 */
export function RequiredStateText({ id }: RequiredStateTextProps) {
  return (
    <span
      className='ds-u-visibility--screen-reader'
      id={id}>
      {/*
        The leading space is load-bearing. Where the name is computed from the label's
        subtree (RadioGroup's legend, FileUpload's <label for>), accname concatenates
        inline nodes without separating them, so the word welded onto the question as
        "Do you still receive this pay?required" — the very defect #492 removed. Where
        the name comes from `aria-labelledby` instead, the ids are already joined with a
        space and the extra one is normalised away.
      */}
      {` ${REQUIRED_STATE_TEXT}`}
    </span>
  );
}

export default RequiredStateText;
